import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Unlock, Loader2, Check, X, Inbox, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });

const NOTES = ['', 'Program client', 'Free tester', 'Giveaway winner', 'Complimentary', 'Partner'];

const Toggle = ({ on, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} aria-pressed={on}
    className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${on ? 'bg-emerald-500' : 'bg-slate-300'}`}>
    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
  </button>
);

export function ToolAccessPanel({ clientId }) {
  const [data, setData] = useState(null);
  const [brands, setBrands] = useState([]);
  const [pkgKey, setPkgKey] = useState('');
  useEffect(() => {
    ax.get(`${API}/api/admin/clients/${clientId}/tool-access`).then((r) => setData(r.data)).catch(() => {});
    ax.get(`${API}/api/admin/coaching-packages`).then((r) => setBrands(r.data.brands || [])).catch(() => {});
  }, [clientId]);
  const put = async (body) => {
    try { const r = await ax.put(`${API}/api/admin/clients/${clientId}/tool-access`, body); setData(r.data); }
    catch { toast.error('Could not update access'); }
  };
  const applyPkg = async () => {
    const [brand_slug, package_id] = pkgKey.split('::');
    try { const r = await ax.post(`${API}/api/admin/clients/${clientId}/tool-access/package`, { brand_slug, package_id }); setData((d) => ({ ...d, tools: r.data.tools })); toast.success(r.data.granted.length ? 'Package tools granted' : 'That package has no tools selected yet'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not apply package'); }
  };
  const viewAs = async () => {
    try { await ax.post(`${API}/api/admin/view-as/${data.user_id || clientId}`); window.location.href = '/portal'; }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not open client view'); }
  };
  if (!data) return <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg mb-6"><Loader2 className="animate-spin text-[#1D4ED8]" size={18} /></div>;
  const all = Object.keys(data.labels);
  const memberTools = data.membership?.status === 'active' ? data.membership.tools || [] : [];
  const pkgs = brands.flatMap((b) => (b.packages || []).map((p) => ({ key: `${b.slug}::${p.package_id}`, label: `${p.name}${b.name ? ` (${b.name})` : ''}`, n: (p.tools_included || []).length })));
  return (
    <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg mb-6" data-testid="tool-access-panel">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="font-bold text-[#0A0F1C]">Tool Access</h3>
        <button type="button" onClick={viewAs} disabled={!data.user_id} data-testid="tool-access-view-as" title={data.user_id ? '' : 'Client has no portal account yet'}
          className="inline-flex items-center gap-1 text-xs font-bold text-white bg-[#F97316] hover:bg-[#EA580C] px-2.5 py-1 rounded-full disabled:opacity-40"><Eye size={12} /> View as client</button>
      </div>
      <div className="flex gap-3 mb-3 text-xs">
        <button type="button" onClick={() => put({ tools: Object.fromEntries(all.map((k) => [k, true])), social_studio: true })} data-testid="tool-access-grant-all" className="font-bold text-[#1D4ED8] hover:underline">Grant everything</button>
        <button type="button" onClick={() => put({ tools: Object.fromEntries(all.map((k) => [k, false])), social_studio: false })} data-testid="tool-access-revoke-all" className="font-bold text-red-500 hover:underline">Revoke all</button>
      </div>
      {data.membership && (
        <div className={`text-xs rounded-lg px-3 py-2 mb-3 ${data.membership.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-500'}`} data-testid="tool-access-membership">
          Membership: <strong>{data.membership.name}</strong> (${data.membership.price}/mo) · {data.membership.status}
        </div>
      )}
      <div className="space-y-2">
        {all.map((k) => {
          const viaMember = memberTools.includes(k);
          const on = !!data.manual?.[k];
          return (
            <div key={k} className="flex items-center justify-between gap-2">
              <span className="text-sm text-[#334155] flex items-center gap-1.5">{on || viaMember ? <Unlock size={13} className="text-emerald-600" /> : <Lock size={13} className="text-[#F97316]" />}{data.labels[k]}
                {viaMember && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 rounded">membership</span>}</span>
              <Toggle on={on} onClick={() => put({ tools: { [k]: !on } })} testid={`tool-access-toggle-${k}`} />
            </div>
          );
        })}
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#F1F5F9]">
          <span className="text-sm text-[#334155] flex items-center gap-1.5">{data.social_studio ? <Unlock size={13} className="text-emerald-600" /> : <Lock size={13} className="text-[#F97316]" />}Social Studio</span>
          <Toggle on={data.social_studio} onClick={() => put({ social_studio: !data.social_studio })} testid="tool-access-toggle-social_studio" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-[#F1F5F9]">
        <label className="text-[11px] font-bold uppercase text-[#94A3B8]">Access reason
          <select value={data.access_note || ''} onChange={(e) => put({ access_note: e.target.value })} data-testid="tool-access-note" className="mt-1 w-full px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs bg-white normal-case font-normal text-[#334155]">
            {NOTES.map((n) => <option key={n} value={n}>{n || 'None'}</option>)}
          </select>
        </label>
        <label className="text-[11px] font-bold uppercase text-[#94A3B8]">Businesses
          <select value={data.business_limit || ''} onChange={(e) => put({ business_limit: e.target.value ? Number(e.target.value) : null })} data-testid="tool-access-business-limit" className="mt-1 w-full px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs bg-white normal-case font-normal text-[#334155]">
            <option value="">Unlimited</option><option value="1">1 business</option><option value="2">2 businesses</option><option value="3">3 businesses</option>
          </select>
        </label>
      </div>
      {pkgs.length > 0 && (
        <div className="mt-4 pt-3 border-t border-[#F1F5F9]">
          <div className="text-[11px] font-bold uppercase text-[#94A3B8] mb-1">Apply a package's tools</div>
          <div className="flex gap-2">
            <select value={pkgKey} onChange={(e) => setPkgKey(e.target.value)} data-testid="tool-access-package-select" className="flex-1 min-w-0 px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs bg-white">
              <option value="">Choose a package…</option>
              {pkgs.map((p) => <option key={p.key} value={p.key}>{p.label} · {p.n} tools</option>)}
            </select>
            <button type="button" onClick={applyPkg} disabled={!pkgKey} data-testid="tool-access-package-apply" className="px-3 py-1.5 rounded-lg bg-[#1D4ED8] text-white text-xs font-bold disabled:opacity-50">Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ToolAccessRequests() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState('');
  const load = () => ax.get(`${API}/api/admin/tool-access/requests`).then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    setBusy(id);
    try { await ax.post(`${API}/api/admin/tool-access/requests/${id}/${action}`); setRows((r) => r.filter((x) => x.request_id !== id)); toast.success(action === 'approve' ? 'Approved, client emailed' : 'Request declined'); }
    catch { toast.error('Could not update request'); }
    finally { setBusy(''); }
  };
  if (!rows.length) return null;
  return (
    <div className="bg-white border border-[#FED7AA] rounded-xl p-4 mb-6" data-testid="tool-access-requests">
      <div className="flex items-center gap-2 mb-3"><Inbox size={16} className="text-[#F97316]" /><h3 className="font-bold text-[#0A0F1C]">Tool access requests ({rows.length})</h3></div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.request_id} className="flex flex-wrap items-center gap-3 border border-[#F1F5F9] rounded-lg px-3 py-2" data-testid={`tool-request-${r.request_id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-semibold text-[#0A0F1C]">{r.client_name} <span className="font-normal text-[#64748B]">wants</span> {r.tool_label}</div>
              <div className="text-xs text-[#94A3B8]">{r.client_email} · {new Date(r.created_at).toLocaleDateString()}{r.note ? ` · “${r.note}”` : ''}</div>
            </div>
            <button type="button" onClick={() => act(r.request_id, 'approve')} disabled={busy === r.request_id} data-testid={`tool-request-approve-${r.request_id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold disabled:opacity-60">{busy === r.request_id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Approve</button>
            <button type="button" onClick={() => act(r.request_id, 'deny')} disabled={busy === r.request_id} data-testid={`tool-request-deny-${r.request_id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold"><X size={12} /> Decline</button>
          </div>
        ))}
      </div>
    </div>
  );
}
